/**
 * Animation helper that creates animation frames for a given duration to aid in animating from one state to another.
 */
export class Animator {
    /**
     * Determines whether or not the animation is in progress.
     */
    get isAnimating() {
        return this.startTime !== undefined && !this.isCancelled && !this.animationEnded;
    }
    /**
     * Creates an animator instance.
     * @param startState The initial state of the animation.
     * @param endState The final state of the animation.
     * @param duration The duration of the animation.
     * @param onAnimationFrameCallback The callback function to be called on each animation frame.
     */
    constructor(startState, endState, duration, onAnimationFrameCallback) {
        this.startState = startState;
        this.endState = endState;
        this.duration = duration;
        this.onAnimationFrameCallback = onAnimationFrameCallback;
        /**
         * Determines whether or not the animation has been cancelled.
         */
        this.isCancelled = false;
        /**
         * Creates an animation frame and repeats in a loop until the animation is complete or cancelled.
         */
        this.animate = () => {
            this.onAnimationFrameCallback({
                progress: this.currentProgress,
                isFinalFrame: this.animationEnded,
                startState: this.startState,
                endState: this.endState
            });
            if (this.isAnimating) {
                this.animationFrameRequestId = requestAnimationFrame(this.animate);
            }
        };
    }
    /**
     * Starts the animation.
     */
    start() {
        this.cancelAnimationFrameRequest();
        const currentTime = this.currentTime;
        this.startTime = currentTime;
        this.endTime = currentTime + this.duration;
        this.isCancelled = false;
        this.animationFrameRequestId = requestAnimationFrame(this.animate);
    }
    /**
     * Cancels the animation.
     */
    cancel() {
        this.cancelAnimationFrameRequest();
        this.isCancelled = true;
    }
    /**
     * Cancels the current animation frame request.
     */
    cancelAnimationFrameRequest() {
        if (this.animationFrameRequestId) {
            cancelAnimationFrame(this.animationFrameRequestId);
        }
    }
    /**
     * Gets the current time in milliseconds.
     */
    get currentTime() {
        return new Date().getTime();
    }
    /**
     * Gets the current progress as a number between 0 and 1.
     */
    get currentProgress() {
        if (this.startTime === undefined || this.endTime === undefined)
            return 0;
        return Math.min((this.currentTime - this.startTime) / (this.endTime - this.startTime), 1);
    }
    /**
     * Determines whether or not the animation has ended based on the current time.
     */
    get animationEnded() {
        if (this.endTime === undefined)
            return true;
        return this.currentTime >= this.endTime;
    }
}
export function easeInOutSine(progress, initialValue, finalValue) {
    return ((initialValue - finalValue) / 2) * (Math.cos(Math.PI * progress) - 1) + initialValue;
}
//# sourceMappingURL=animator.js.map